import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Error as ErrorIcon,
  Email,
  Visibility,
  Refresh,
  Schedule,
  Close as CloseIcon,
} from '@mui/icons-material';
import { getMessageHistory, getMessageById, revokeExternalShare } from '../../services/messages';
import { useTheme, useMediaQuery } from '@mui/material';
import { useThemeMode } from '../../contexts/ThemeModeContext';

const MessageHistory = () => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        limit,
        offset: (page - 1) * limit,
      };
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await getMessageHistory(params);
      setMessages(response.messages || []);
      setTotal(response.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch message history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [page, statusFilter]);

  const handleViewDetails = async (messageId) => {
    try {
      const message = await getMessageById(messageId);
      setSelectedMessage(message);
      setDetailDialogOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch message details');
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'sent':
        return (
          <Chip
            icon={<CheckCircle />}
            label="Sent"
            color="success"
            size="small"
          />
        );
      case 'failed':
        return (
          <Chip
            icon={<ErrorIcon />}
            label="Failed"
            color="error"
            size="small"
          />
        );
      case 'pending':
        return (
          <Chip
            icon={<Schedule />}
            label="Pending"
            color="warning"
            size="small"
          />
        );
      case 'bounced':
        return (
          <Chip
            icon={<ErrorIcon />}
            label="Bounced"
            color="error"
            size="small"
          />
        );
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const filteredMessages = messages.filter((msg) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      msg.recipientEmail?.toLowerCase().includes(search) ||
      msg.patientName?.toLowerCase().includes(search) ||
      msg.patientId?.toLowerCase().includes(search) ||
      msg.subject?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Container maxWidth="xl" sx={{ py: 1, px: { xs: 1, sm: 1.5 } }}>
      <Box mb={1} display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '0.95rem', lineHeight: 1.2 }}>
            Message History
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.72rem' }}>
            Track all emails sent with secure medical imaging files
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: '6px 10px', mb: 1.5, borderRadius: '6px' }}>
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            placeholder="Search by email, patient name, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 16 }} />
                </InputAdornment>
              ),
              sx: { height: 28, fontSize: '0.75rem' }
            }}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel sx={{ fontSize: '0.75rem', top: -4 }}>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              sx={{ height: 28, fontSize: '0.75rem' }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Status</MenuItem>
              <MenuItem value="sent" sx={{ fontSize: '0.75rem' }}>Sent</MenuItem>
              <MenuItem value="failed" sx={{ fontSize: '0.75rem' }}>Failed</MenuItem>
              <MenuItem value="pending" sx={{ fontSize: '0.75rem' }}>Pending</MenuItem>
              <MenuItem value="bounced" sx={{ fontSize: '0.75rem' }}>Bounced</MenuItem>
            </Select>
          </FormControl>

          <IconButton onClick={fetchMessages} disabled={loading} size="small" sx={{ height: 28, width: 28 }}>
            <Refresh sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.25, borderRadius: '6px', fontSize: '0.75rem' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : filteredMessages.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '6px' }}>
          <Email sx={{ fontSize: 48, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
          <Typography variant="body2" color="textSecondary" gutterBottom fontWeight={600}>
            No messages found
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Start sending secure emails to see your message history here'}
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: '6px', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { py: '5px !important', px: '8px !important', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' } }}>
                  <TableCell>Sent At</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Attachments</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMessages.map((message) => (
                  <TableRow key={message._id} hover sx={{ '& td': { py: '4px !important', px: '8px !important', fontSize: '0.75rem' } }}>
                    <TableCell>
                      {formatDate(message.sentAt)}
                    </TableCell>
                    <TableCell>{message.recipientEmail}</TableCell>
                    <TableCell>
                      {message.patientName || message.patientId}
                      {message.patientId && (
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.7rem' }}>
                          ID: {message.patientId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontSize: '0.75rem' }}>
                        {message.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {message.attachments?.image && (
                          <Chip label="Image" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                        )}
                        {message.attachments?.report && (
                          <Chip label="Report" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{getStatusChip(message.status)}</TableCell>
                    <TableCell align="right">
                      {message.externalShare && <Button size="small" onClick={async () => {
                        try { await revokeExternalShare(message.externalShare); await fetchMessages(); }
                        catch { setError('Unable to revoke shared access.'); }
                      }}>Revoke access</Button>}
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(message._id)}
                          sx={{ height: 26, width: 26 }}
                        >
                          <Visibility sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Message Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '640px', md: '700px' },
            maxWidth: '720px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
            bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#F1F5F9' : '#0F172A',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Email color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>Message Details</Typography>
          </Box>
          <IconButton size="small" onClick={() => setDetailDialogOpen(false)} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 2, overflowY: 'auto' }}>
          {selectedMessage && (
            <Grid container spacing={1.75}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : 'text.secondary', fontWeight: 700, display: 'block', mb: 0.25 }}>
                  STATUS
                </Typography>
                <Box>{getStatusChip(selectedMessage.status)}</Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : 'text.secondary', fontWeight: 700, display: 'block', mb: 0.25 }}>
                  SENT AT
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(selectedMessage.sentAt)}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : 'text.secondary', fontWeight: 700, display: 'block', mb: 0.25 }}>
                  RECIPIENT
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedMessage.recipientEmail}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : 'text.secondary', fontWeight: 700, display: 'block', mb: 0.25 }}>
                  PATIENT INFORMATION
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedMessage.patientName} ({selectedMessage.patientId})
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : 'text.secondary', fontWeight: 700, display: 'block', mb: 0.25 }}>
                  SUBJECT
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedMessage.subject}</Typography>
              </Grid>

              {selectedMessage.note && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : 'text.secondary', fontWeight: 700, display: 'block', mb: 0.25 }}>
                    NOTE
                  </Typography>
                  <Typography variant="body2">{selectedMessage.note}</Typography>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : 'text.secondary', fontWeight: 700, display: 'block', mb: 0.25 }}>
                  ATTACHMENTS
                </Typography>
                <Box display="flex" gap={1} mt={0.5}>
                  {selectedMessage.attachments?.image && (
                    <Chip label="Image" size="small" />
                  )}
                  {selectedMessage.attachments?.report && (
                    <Chip label="Report" size="small" />
                  )}
                  {!selectedMessage.attachments?.image && !selectedMessage.attachments?.report && (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </Box>
              </Grid>

              {selectedMessage.emailProviderResponse?.messageId && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : 'text.secondary', fontWeight: 700, display: 'block', mb: 0.25 }}>
                    EMAIL MESSAGE ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {selectedMessage.emailProviderResponse.messageId}
                  </Typography>
                </Grid>
              )}

              {selectedMessage.emailProviderResponse?.error && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">Error:</Typography>
                    <Typography variant="body2">{selectedMessage.emailProviderResponse.error}</Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider', display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setDetailDialogOpen(false)} variant="contained" sx={{ px: 2.5 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MessageHistory;

